import { ArrowRight, Rocket, X } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, ErrorState, IconButton, Pressable, cn, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useConnection } from '../../lib/store.ts';
import { claimAutoOpen, updateOnboarding, useOnboarding, useOnboardingData } from './client.ts';
import { NEXT_ACTION } from './model.ts';
import { StepStatusIcon } from './parts.tsx';
import { useOnboardingView } from './useOnboardingView.ts';

/**
 * Getting started on Overview: the five steps with where each stands, and the one action
 * that moves you on. Shows on a fresh hub (no projects, or only its own machine) until every
 * step is done or skipped, or someone hides it. A brand new hub opens Getting started once.
 */
export function OnboardingChecklist() {
  const navigate = useNavigate();
  const { phase, mode } = useConnection();
  useOnboardingData();
  const view = useOnboardingView();
  const dismissedAt = useOnboarding((s) => s.state.dismissedAt);
  // Hiding it hides it for everyone on this hub, so viewers cannot.
  const canHide = useOnboarding((s) => s.probes.auth?.me?.role !== 'viewer');
  /** Why hiding the checklist failed: shown in the card, never in a toast. */
  const [hideError, setHideError] = useState<UiError | null>(null);
  /** Hidden in this visit: a quiet line with Undo stands where the card was (no toast). */
  const [justHidden, setJustHidden] = useState(false);
  const [undoError, setUndoError] = useState<UiError | null>(null);

  // A brand new hub opens Getting started by itself, once per tab. Demo data is a busy hub: never.
  const autoOpen = phase === 'ready' && view.phase === 'ready' && view.autoOpen && mode === 'live';
  useEffect(() => {
    if (autoOpen && claimAutoOpen()) navigate('/getting-started', { replace: true });
  }, [autoOpen, navigate]);

  if (justHidden && dismissedAt != null) {
    const undo = async () => {
      setUndoError(null);
      try {
        await updateOnboarding({ op: 'reopen' });
        setJustHidden(false);
      } catch (e) {
        setUndoError(toUiError(e));
      }
    };
    return (
      <section aria-label="Getting started hidden" className="rounded-card border border-border bg-surface px-4 py-2">
        <div className="flex items-center gap-3">
          <Rocket className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <p className="truncate text-ui text-fg-muted">Checklist hidden</p>
          <p className="hidden truncate text-caption text-fg-subtle sm:block">In Settings, Getting started</p>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => void undo()}>
            Undo
          </Button>
          <IconButton label="Close" icon={<X />} size="sm" onClick={() => setJustHidden(false)} />
        </div>
        {undoError && <ErrorState variant="inline" error={undoError} onRetry={() => void undo()} />}
      </section>
    );
  }

  if (phase !== 'ready' || view.phase !== 'ready' || !view.showChecklist || dismissedAt != null) return null;

  const current = view.current;
  const hide = async () => {
    setHideError(null);
    try {
      await updateOnboarding({ op: 'dismiss' });
      setJustHidden(true);
    } catch (e) {
      setHideError(toUiError(e));
    }
  };

  return (
    <section aria-labelledby="checklist-title" className="rounded-card border border-border bg-surface surface-sheen shadow-elevation-flat">
      <header className="flex items-center gap-3 pb-1 pl-4 pr-2 pt-3">
        <Rocket className="size-4 shrink-0 text-accent-text" aria-hidden />
        <h2 id="checklist-title" className="truncate text-ui font-medium text-fg">
          Getting started
        </h2>
        <span className="text-ui text-fg-subtle num">
          {view.done} of {view.total}
        </span>
        <span className="ml-auto flex items-center gap-1">
          {current && (
            // On a phone-width screen the highlighted step below is the way on; the button would squeeze the title.
            <Button size="sm" className="hidden sm:inline-flex" iconRight={<ArrowRight />} onClick={() => navigate(`/getting-started?step=${current}`)}>
              {NEXT_ACTION[current]}
            </Button>
          )}
          {canHide && <IconButton label="Hide checklist" icon={<X />} size="sm" onClick={hide} />}
        </span>
      </header>
      {hideError && (
        <div className="px-4 pb-3">
          <ErrorState variant="inline" error={hideError} onRetry={hide} />
        </div>
      )}
      {/* A stepper: each step's node joined to the next by a line that fills as steps are done. */}
      <ol className="grid grid-cols-1 gap-1 p-2 xl:grid-cols-5 xl:gap-0">
        {view.steps.map((s, i) => {
          const done = s.status === 'done' || s.status === 'skipped';
          const last = i === view.steps.length - 1;
          return (
            <li key={s.id} className="relative min-w-0">
              <Pressable
                onClick={() => navigate(`/getting-started?step=${s.id}`)}
                className={cn('relative flex h-full w-full items-start gap-3 rounded-control px-3 py-3 text-left transition-colors xl:flex-col xl:gap-2 duration-(--duration-fast) hover:bg-hover', s.status === 'current' && 'bg-active')}
                aria-current={s.status === 'current' ? 'step' : undefined}
              >
                <span className="flex size-6 shrink-0 items-center justify-center rounded-pill bg-surface">
                  <StepStatusIcon status={s.status} bare />
                </span>
                <span className="min-w-0 max-w-full">
                  {/* The name is the visible text (WCAG 2.5.3, axe label-content-name-mismatch), with the step number and a separator for screen readers. */}
                  <span className="sr-only">{`Step ${i + 1}, `}</span>
                  <span className={cn('block truncate text-ui', done ? 'text-fg-muted' : 'font-medium text-fg')}>{s.title}</span>
                  <span className="sr-only">: </span>
                  <span className="block truncate text-caption text-fg-subtle">{s.detail}</span>
                </span>
              </Pressable>
              {!last && (
                <span
                  aria-hidden
                  className={cn('pointer-events-none absolute -right-3 left-9 top-6 hidden h-px transition-colors duration-(--duration-slow) xl:block', done ? 'bg-accent' : 'bg-border')}
                />
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
